import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, exhaustMap, filter, map } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { EnvironmentService, EventService, GridService, ListService, NotificationService, fromList } from '@core/services';
import { errorDetailBlob } from '@util/http';
import { currentTimeInSeconds } from '@util/list';

import { MultipleListOfValuesResponse } from '#types/list';
import { ReportStatusResponseRow } from '#types/report';

import { AuthActions } from '@auth/state';
import { CONSTANTS } from '@report/constants';
import { ReportService } from '@report/services';

import * as ReportActions from './report.actions';

@Injectable()
export class ReportEffects {
  protected actions$ = inject(Actions);
  protected store = inject(Store);
  protected router = inject(Router);
  protected translocoService = inject(TranslocoService);
  protected reportService = inject(ReportService);
  protected gridService = inject(GridService);
  protected environmentService = inject(EnvironmentService);
  protected notificationService = inject(NotificationService);
  protected listService = inject(ListService);
  protected eventService = inject(EventService);

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  Initialize$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportActions.LookupInitialize, ReportActions.CompletedInitialize),
      map((screen) => AuthActions.ChangeScreen(screen))
    );
  });

  // lists

  RequestLists$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ReportActions.RequestLists),
        concatLatestFrom(() => [this.store.select(fromList.selectReportLists)]),
        map(([_, lists]) => {
          let lastUpdated = -1;

          for (const list of Object.keys(lists)) {
            // skip parameterized lists
            if (lists[list] && !Array.isArray(lists[list].data)) {
              continue;
            }

            if (lists[list]) {
              lastUpdated = Math.max(lastUpdated, currentTimeInSeconds() - (lists[list].updated ?? 0));
            }
          }

          if (lastUpdated === -1 || lastUpdated > this.environmentService.settings.secondsToCacheLists) {
            this.store.dispatch(ReportActions.RequestReportLists());
          }
        })
      );
    },
    { dispatch: false }
  );

  RequestListsOnChangeScreen$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.ChangeScreen),
        filter((data) => data?.screen?.group === CONSTANTS.SCREEN_GROUP),
        concatLatestFrom(() => [this.store.select(fromList.selectReportLists)]),
        map(([_, lists]) => {
          let lastUpdated = -1;

          for (const list of Object.keys(lists)) {
            // skip parameterized lists
            if (lists[list] && !Array.isArray(lists[list].data)) {
              continue;
            }

            if (lists[list]) {
              lastUpdated = Math.max(lastUpdated, currentTimeInSeconds() - (lists[list].updated ?? 0));
            }
          }

          if (lastUpdated === -1 || lastUpdated > this.environmentService.settings.secondsToCacheLists) {
            this.store.dispatch(ReportActions.RequestReportLists());
          }
        })
      );
    },
    { dispatch: false }
  );

  RequestReportLists$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportActions.RequestReportLists),
      exhaustMap(() => {
        return this.listService.getListOfValues<MultipleListOfValuesResponse>('report').pipe(
          this.eventService.indicate(CONSTANTS.LIST_ALL),
          map((lists) => ReportActions.RequestReportListsSuccess({ lists })),
          catchError((error) => of(ReportActions.RequestReportListsFailure(error)))
        );
      })
    );
  });

  // grids

  LookupRequestData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportActions.LookupRequestData, ReportActions.LookupRequestDataWithFilters, ReportActions.LookupRequestDataWithParameters),
      concatLatestFrom(({ id }) => [
        this.gridService.filters$(id),
        this.gridService.page$(id),
        this.gridService.rows$(id),
        this.gridService.sortsObject$(id),
      ]),
      exhaustMap(([{ id }, filters, page, rows, sorts]) =>
        this.reportService.reportLookup({ filters, page, rows, sorts }).pipe(
          map((data) => ReportActions.LookupRequestDataSuccess({ id, data })),
          catchError((_error) => of(ReportActions.LookupRequestDataFailure({ id })))
        )
      )
    );
  });

  CompletedReportRequestData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        ReportActions.CompletedRequestData,
        ReportActions.CompletedRequestDataWithFilters,
        ReportActions.CompletedRequestDataWithParameters
      ),
      concatLatestFrom(({ id }) => [
        this.gridService.filters$(id),
        this.gridService.page$(id),
        this.gridService.rows$(id),
        this.gridService.sortsObject$(id),
      ]),
      exhaustMap(([{ id }, filters, page, rows, sorts]) =>
        this.reportService.reportCompleted({ filters, page, rows, sorts }).pipe(
          map((data) => ReportActions.CompletedRequestDataSuccess({ id, data })),
          catchError((_error) => of(ReportActions.CompletedRequestDataFailure({ id })))
        )
      )
    );
  });

  // run report effects

  RequestReportGetParameters$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportActions.RequestReportGetParameters),
      exhaustMap(({ reportName }) =>
        this.reportService.reportGetParams({ reportName }).pipe(
          map((data) => ReportActions.RequestReportGetParametersSuccess({ reportName, data })),
          catchError((error) => of(ReportActions.RequestReportGetParametersFailure({ reportName, error })))
        )
      )
    );
  });

  RequestReportGetParametersFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ReportActions.RequestReportGetParametersFailure),
        map(({ error }) => {
          this.notificationService.error({
            title: this.translate('report.run_report.error.title'),
            message: error?.error?.error?.message ?? this.translate('report.run_report.error.get_parameters_failed.message'),
          });
        })
      );
    },
    { dispatch: false }
  );

  RequestReportGetParametersRefresh$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportActions.RequestReportGetParametersRefresh),
      exhaustMap(({ reportName }) =>
        this.reportService.reportGetParams({ reportName }).pipe(
          map((data) => ReportActions.RequestReportGetParametersRefreshSuccess({ reportName, data })),
          catchError((error) => of(ReportActions.RequestReportGetParametersRefreshFailure({ reportName, error })))
        )
      )
    );
  });

  RequestReportSetParameters$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportActions.RequestReportSetParameters),
      concatLatestFrom(() => [this.reportService.requestId$, this.reportService.reportName$]),
      exhaustMap(([{ format, parameters }, requestId, reportName]) => {
        this.reportService.reportLoading$.next(true);

        return this.reportService.reportSetParams({ requestId, reportName, params: parameters }).pipe(
          map((data) => ReportActions.RequestReportSetParametersSuccess({ format, data })),
          catchError((error) => of(ReportActions.RequestReportSetParametersFailure({ error })))
        );
      })
    );
  });

  RequestReportSetParametersSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportActions.RequestReportSetParametersSuccess),
      map(() => ReportActions.RequestReportRunReport())
    );
  });

  RequestReportSetParametersFailure$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportActions.RequestReportSetParametersFailure),
      concatLatestFrom(() => [this.reportService.reportName$]),
      map(([{ error }, reportName]) => {
        this.notificationService.error({
          title: this.translate('report.run_report.error.title'),
          message: error?.error?.error?.message,
        });

        return ReportActions.RequestReportGetParametersRefresh({ reportName });
      })
    );
  });

  RequestReportRunReport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportActions.RequestReportRunReport),
      concatLatestFrom(() => [this.reportService.requestId$, this.reportService.reportName$, this.reportService.format$]),
      exhaustMap(([_, requestId, reportName, format]) =>
        this.reportService.reportGenerate({ requestId, reportName, format }).pipe(
          map((data) => ReportActions.RequestReportRunReportSuccess({ data })),
          catchError((error) => of(ReportActions.RequestReportRunReportFailure({ error })))
        )
      )
    );
  });

  RequestReportRunReportSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportActions.RequestReportRunReportSuccess),
      concatLatestFrom(() => [this.reportService.filename$, this.reportService.format$]),
      map(([{ data }, filename, format]) => {
        this.reportService.reportLoading$.next(false);
        this.reportService.displayRunReportDialog$.next(false);

        this.notificationService.info({
          id: 'report-generated',
          title: this.translate('report.run_report.generate.title'),
          message: this.translate('report.run_report.generate.message'),
          progressBar: true,
          closable: true,
          duration: 10000,
          actionLabel: this.translate('report.status.action_label'),
          action: () => {
            this.gridService.gridRefresh$.next(CONSTANTS.GRID_REPORT_COMPLETED);
            void this.router.navigate(['/reports/completed']);
          },
        });

        return ReportActions.PendingReportAdd({ requestId: data.requestId, filename, format });
      })
    );
  });

  RequestReportRunReportFailure$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportActions.RequestReportRunReportFailure),
      concatLatestFrom(() => [this.reportService.reportName$]),
      map(([{ error }, reportName]) => {
        this.notificationService.error({
          title: this.translate('report.run_report.error.title'),
          message: error?.error?.error?.message,
        });

        return ReportActions.RequestReportGetParametersRefresh({ reportName });
      })
    );
  });

  RequestReportDownload$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportActions.RequestReportDownload),
      exhaustMap(({ requestId, format, fileName }) =>
        this.reportService.reportDownload({ requestId, format }).pipe(
          map((data) => ReportActions.RequestReportDownloadSuccess({ requestId, fileName, data })),
          catchError((error) => of(ReportActions.RequestReportDownloadFailure({ error })))
        )
      )
    );
  });

  RequestReportDownloadSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ReportActions.RequestReportDownloadSuccess),
        map(({ data, fileName }) => {
          if (fileName.endsWith('xls-data')) {
            fileName += '.xls';
          }
          this.reportService.download(fileName, 'application/octet-stream', data);
          this.gridService.gridRefresh$.next(CONSTANTS.GRID_REPORT_COMPLETED);
        })
      );
    },
    { dispatch: false }
  );

  RequestReportDownloadFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ReportActions.RequestReportDownloadFailure),
        map(({ error }) => {
          errorDetailBlob(error)
            .then((data) => {
              let message: string;

              switch (data.message) {
                case 'ORA-01403: no data found':
                case 'report-not-found-in-cache':
                  message = this.translate('report.run_report.error.report_not_found_in_cache.message', { requestId: data.requestId });
                  break;

                default:
                  message = data.message;
                  break;
              }

              this.notificationService.error({
                message,
              });
            })
            .catch((err) => {
              console.error(err);
            });
        })
      );
    },
    { dispatch: false }
  );

  RequestReportFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          ReportActions.RequestReportSetParametersFailure,
          ReportActions.RequestReportRunReportFailure,
          ReportActions.RequestReportDownloadFailure
        ),
        map(() => {
          this.reportService.reportLoading$.next(false);
        })
      );
    },
    { dispatch: false }
  );

  RequestReportRefresh$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportActions.RequestReportRefresh),
      exhaustMap(() =>
        this.reportService.reportRefresh({}).pipe(
          map((data) => ReportActions.RequestReportRefreshSuccess({ data })),
          catchError((error) => of(ReportActions.RequestReportRefreshFailure({ error })))
        )
      )
    );
  });

  RequestReportRefreshSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportActions.RequestReportRefreshSuccess),
      map(({ data }) => {
        const count = data[0]?.loaded_report_count ?? 0;

        this.notificationService.info({
          title: this.translate('report.lookup.toolbar.refresh_reports.success.title'),
          message: this.translate('report.lookup.toolbar.refresh_reports.success.message', { count: `${count}` }),
          progressBar: true,
          closable: true,
          duration: 10000,
        });

        // refresh the grid
        this.gridService.gridRefresh$.next(CONSTANTS.GRID_REPORT_LOOKUP);

        // update report module lists (dropdown values)
        return ReportActions.RequestReportLists();
      })
    );
  });

  RequestReportRefreshFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ReportActions.RequestReportRefreshFailure),
        map(() => {
          this.notificationService.error({
            title: this.translate('report.lookup.toolbar.refresh_reports.failure.title'),
            message: this.translate('report.lookup.toolbar.refresh_reports.failure.message'),
          });
        })
      );
    },
    { dispatch: false }
  );

  RequestReportStatus$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportActions.RequestReportStatus),
      exhaustMap(({ pendingRequests }) =>
        this.reportService.reportStatus({ request_id_list: [...pendingRequests] }).pipe(
          map((data) => ReportActions.RequestReportStatusSuccess({ data })),
          catchError((error) => of(ReportActions.RequestReportStatusFailure({ error })))
        )
      )
    );
  });

  RequestReportStatusSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportActions.RequestReportStatusSuccess),
      filter((x) => !!x),
      map(({ data }) => {
        let title: string;
        let message: string;
        let entry: ReportStatusResponseRow;

        // check for failed reports and return an error
        if ((entry = data.find((x) => x.status === 'error')) !== undefined) {
          this.notificationService.remove('report-generated');

          title = this.translate('report.status.error_single.title');
          message = this.translate('report.status.error_single.message');

          this.notificationService.error({
            title,
            message,
            progressBar: true,
            closable: true,
            duration: 10000,
            actionLabel: this.translate('report.status.action_label'),
            action: () => {
              this.gridService.gridRefresh$.next(CONSTANTS.GRID_REPORT_COMPLETED);
              void this.router.navigate(['/reports/completed']);
            },
          });

          // if a report errors out, return and do not process any more records
          return ReportActions.PendingReportRemove({ requestId: entry.request_id });
        }

        if ((entry = data.find((x) => x.status === 'complete')) !== undefined) {
          this.notificationService.remove('report-generated');

          if (data.length === 1) {
            title = this.translate('report.status.report_ready_single.title');
            message = this.translate('report.status.report_ready_single.message');
          } else {
            title = this.translate('report.status.report_ready_multiple.title');
            message = this.translate('report.status.report_ready_multiple.message');
          }

          this.notificationService.info({
            title,
            message,
            progressBar: true,
            closable: true,
            duration: 10000,
            actionLabel: this.translate('report.status.action_label'),
            action: () => {
              this.gridService.gridRefresh$.next(CONSTANTS.GRID_REPORT_COMPLETED);
              void this.router.navigate(['/reports/completed']);
            },
          });

          this.store.dispatch(ReportActions.PendingReportRemove({ requestId: entry.request_id }));
        }

        // remove all complete or error reports
        for (const row of data) {
          if (row.status === 'complete' || row.status === 'error') {
            this.store.dispatch(ReportActions.PendingReportRemove({ requestId: row.request_id }));
          }
        }

        return ReportActions.RequestReportStatusDone();
      })
    );
  });
}
